import { executeQuery } from './salesforce/client';

/**
 * Demo events pulled for the Demos Booked metric, de-duplicated client-side.
 *
 * The org holds multiple Event records per real demo: the Calendly-created
 * event plus calendar-sync copies (sometimes several per attendee calendar,
 * with differing WhoIds and subject formats). Counting raw rows overstates
 * demos ~1.7x; counting only Calendly rows drops demos booked directly on a
 * rep's calendar. So we fetch every non-canceled demo event and collapse
 * copies onto a key of start datetime + normalized prospect name.
 *
 * Datetime alone is not enough — two AEs can run demos in the same slot —
 * and WhoId is unreliable (sync copies of one demo link different contacts).
 */
function demoDedupKey(subject: string, activityDateTime: string): string {
  let s = subject.toLowerCase();
  // Strip the Calendly suffix: "... - Calendly - <uuid>"
  s = s.replace(/calendly\s*-\s*[0-9a-f]{8}-[0-9a-f-]{27}/g, ' ');
  // "Prospect <> AE" — keep only the prospect side
  s = s.split('<>')[0];
  // Remove boilerplate so "Jane Doe: EncorEstate Plans Demo" and
  // "EncorEstate Plans Demo | Jane Doe" normalize to the same name
  s = s.replace(/encorestate plans/g, ' ');
  s = s.replace(/initial conversation|conversation|demo|calendly/g, ' ');
  s = s.replace(/[^a-z0-9]+/g, ' ').trim();
  return `${activityDateTime}__${s}`;
}

export async function fetchDemoEvents(
  startDate: string,
  signal?: AbortSignal
): Promise<Record<string, unknown>[]> {
  const soql = `SELECT Subject, ActivityDate, ActivityDateTime FROM Event WHERE Subject LIKE '%Demo%' AND (NOT Subject LIKE '%Canceled%') AND ActivityDate >= ${startDate}`;
  const rows = await executeQuery<Record<string, unknown>>(soql, signal);
  return rows.map((r) => ({
    ActivityDate: r.ActivityDate,
    dedupKey: demoDedupKey(String(r.Subject ?? ''), String(r.ActivityDateTime ?? '')),
  }));
}
